/**
 * Hash router — TASK 7.3 Step 1.
 *
 * Extracted from the mock's `hashchange` wiring and path helpers
 * (mock/SteamyMusic.dc.html L871-880, L985-1005):
 *
 *   _q()      -> {@link queryOf}     `new URLSearchParams(...)` from the hash
 *   _qOf(h)   -> {@link queryStringOf}
 *   _path()   -> {@link pathOf}      strip `?…`, default `/`, drop trailing `/`
 *   _go(h)    -> {@link navigate}
 *   the `if(path===…)` cascade in `renderVals()` -> the {@link ROUTES} table
 *
 * The cascade became a table because the cascade was ALSO the sim layer's
 * dispatcher (it called `this._home(V,q)` etc.). Those view builders belong to
 * Stories 8-10; what TASK 7.3 owns is resolution — hash in, route + params out
 * — plus the `<h1 data-h1 tabindex="-1">` page heading every route must carry
 * (ui-contract §3 shell table, `page-heading`).
 *
 * ── ROUTE COUNT ───────────────────────────────────────────────────────────
 * {@link ROUTES} holds the 20 crawled routes of ui-contract §1 (ids 1-20 in
 * that table, `/*` last) PLUS the 4 paths §1's "route reconciliation" note
 * folds into them: the redirect hubs `/browse` and `/admin`, and the
 * supplementally-extracted `/signin/error` and `/not-authorized`. The 20
 * contract routes are exactly those with `contractRoute: true`.
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { el } from '../components/primitives/dom.js';

/**
 * @typedef {object} RouteDefinition
 * @property {string} id stable identifier used by state.route.routeId
 * @property {string} pattern path pattern; `:param` segments capture
 * @property {string} title the `<h1>` text, or '' when the heading is derived
 *   from data the view loads (artist/album/genre/playlist names)
 * @property {string} headingClass class list for the `<h1>` (styles/README §3)
 * @property {string} screenLabel the mock's `data-screen-label`
 * @property {'home'|'browse'|'search'|'faves'|'stats'|'more'|null} navSection
 *   which PrimaryNav item takes `aria-current` (mock L959-965)
 * @property {boolean} shell renders inside the app shell (vs. standalone auth)
 * @property {boolean} contractRoute one of ui-contract §1's 20 crawled routes
 * @property {string} [redirectTo] hub route: navigate here instead
 * @property {boolean} [adminOnly] server-enforced; this flag only drives the
 *   client-side redirect to /not-authorized (NOT an authorization control)
 */

const TITLE = 'sm-page-title';

/** @type {RouteDefinition[]} */
export const ROUTES = [
  {
    id: 'home',
    pattern: '/',
    title: 'ON THE DECKS',
    headingClass: `${TITLE} ${TITLE}--tight ${TITLE}--shadow-pink`,
    screenLabel: 'Home',
    navSection: 'home',
    shell: true,
    contractRoute: true,
  },
  // Redirect hub (ui-contract §1 note: `/browse` -> `/browse/artists`; mock L986)
  {
    id: 'browse',
    pattern: '/browse',
    title: 'BROWSE THE CRATES',
    headingClass: `${TITLE} ${TITLE}--shadow-purple`,
    screenLabel: 'Browse',
    navSection: 'browse',
    shell: true,
    contractRoute: false,
    redirectTo: '/browse/artists',
  },
  {
    id: 'browse-artists',
    pattern: '/browse/artists',
    title: 'BROWSE THE CRATES',
    headingClass: `${TITLE} ${TITLE}--shadow-purple`,
    screenLabel: 'Browse',
    navSection: 'browse',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'browse-genres',
    pattern: '/browse/genres',
    title: 'BROWSE THE CRATES',
    headingClass: `${TITLE} ${TITLE}--shadow-purple`,
    screenLabel: 'Browse',
    navSection: 'browse',
    shell: true,
    contractRoute: true,
  },
  /*
   * The ALBUMS tab. Net-new (not one of the 20 crawled contract routes), added
   * on request: the catalog was only reachable by drilling through an artist or
   * a genre, so an album whose artist you could not remember was unfindable
   * outside search. `contractRoute: false` keeps the §1 count honest.
   */
  {
    id: 'browse-albums',
    pattern: '/browse/albums',
    title: 'BROWSE THE CRATES',
    headingClass: `${TITLE} ${TITLE}--shadow-purple`,
    screenLabel: 'Browse',
    navSection: 'browse',
    shell: true,
    contractRoute: false,
  },
  {
    id: 'artist',
    pattern: '/artists/:artistId',
    title: '',
    headingClass: `${TITLE} ${TITLE}--40 ${TITLE}--flush ${TITLE}--shadow-teal`,
    screenLabel: 'Artist detail',
    navSection: 'browse',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'album',
    pattern: '/albums/:albumId',
    title: '',
    headingClass: `${TITLE} ${TITLE}--34 ${TITLE}--flush ${TITLE}--lh ${TITLE}--shadow-purple`,
    screenLabel: 'Album detail',
    navSection: 'browse',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'genre',
    pattern: '/genres/:genreId',
    title: '',
    headingClass: `${TITLE} ${TITLE}--40 ${TITLE}--flush ${TITLE}--shadow-yellow`,
    screenLabel: 'Genre detail',
    navSection: 'browse',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'search',
    pattern: '/search',
    title: 'DIG THE CRATES',
    headingClass: `${TITLE} ${TITLE}--shadow-teal`,
    screenLabel: 'Search',
    navSection: 'search',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'favorites',
    pattern: '/favorites',
    title: 'HEARTED',
    headingClass: `${TITLE} ${TITLE}--shadow-pink`,
    screenLabel: 'Favorites',
    navSection: 'faves',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'playlists',
    pattern: '/playlists',
    title: 'YOUR MIXES',
    headingClass: `${TITLE} ${TITLE}--flush ${TITLE}--shadow-purple`,
    screenLabel: 'Playlists',
    navSection: 'more',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'playlist',
    pattern: '/playlists/:playlistId',
    title: '',
    headingClass: `${TITLE} ${TITLE}--32 ${TITLE}--flush ${TITLE}--lh ${TITLE}--shadow-pink`,
    screenLabel: 'Playlist detail',
    navSection: 'more',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'downloads',
    pattern: '/downloads',
    title: 'IN YOUR POCKET',
    headingClass: `${TITLE} ${TITLE}--shadow-yellow`,
    screenLabel: 'Downloads',
    navSection: 'more',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'stats',
    pattern: '/stats',
    title: 'YOUR NUMBERS',
    headingClass: `${TITLE} ${TITLE}--shadow-teal`,
    screenLabel: 'Stats',
    navSection: 'stats',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'queue',
    pattern: '/queue',
    title: 'UP NEXT',
    headingClass: `${TITLE} ${TITLE}--shadow-pink`,
    screenLabel: 'Queue',
    navSection: 'more',
    shell: true,
    contractRoute: true,
  },
  /*
   * `/player` renders the full-player DIALOG over the shell (mock L730-768).
   * The mock leaves `main` EMPTY here, so the route has no <h1> at all — an
   * a11y defect the shell contract (ui-contract §3: every route has a
   * `page-heading` focus target) does not tolerate. Fixed the way the mock
   * itself fixes it on /signin (L44): a visually-hidden heading.
   */
  {
    id: 'player',
    pattern: '/player',
    title: 'Now playing',
    headingClass: 'sm-visually-hidden',
    screenLabel: 'Full player',
    navSection: null,
    shell: true,
    contractRoute: true,
  },
  // Redirect hub (ui-contract §1 note: `/admin` -> `/admin/users`; mock L1001)
  {
    id: 'admin',
    pattern: '/admin',
    title: 'THE GUEST LIST',
    headingClass: `${TITLE} ${TITLE}--tight ${TITLE}--shadow-pink`,
    screenLabel: 'Admin users',
    navSection: 'more',
    shell: true,
    contractRoute: false,
    adminOnly: true,
    redirectTo: '/admin/users',
  },
  {
    id: 'admin-users',
    pattern: '/admin/users',
    title: 'THE GUEST LIST',
    headingClass: `${TITLE} ${TITLE}--tight ${TITLE}--shadow-pink`,
    screenLabel: 'Admin users',
    navSection: 'more',
    shell: true,
    contractRoute: true,
    adminOnly: true,
  },
  {
    id: 'admin-sync',
    pattern: '/admin/sync',
    title: 'SYNC ENGINE',
    headingClass: `${TITLE} ${TITLE}--tight ${TITLE}--shadow-teal`,
    screenLabel: 'Admin sync',
    navSection: 'more',
    shell: true,
    contractRoute: true,
    adminOnly: true,
  },
  {
    id: 'settings',
    pattern: '/settings',
    title: 'YOU',
    headingClass: `${TITLE} ${TITLE}--shadow-purple`,
    screenLabel: 'Settings',
    navSection: 'more',
    shell: true,
    contractRoute: true,
  },
  {
    id: 'not-authorized',
    pattern: '/not-authorized',
    title: "You're not authorized to see this",
    headingClass: 'sm-status-title sm-status-title--18',
    screenLabel: 'Not authorized',
    navSection: null,
    shell: true,
    contractRoute: false,
  },
  {
    id: 'signin',
    pattern: '/signin',
    title: 'Sign in',
    headingClass: 'sm-visually-hidden',
    screenLabel: 'Sign in',
    navSection: null,
    shell: false,
    contractRoute: true,
  },
  {
    id: 'signin-denied',
    pattern: '/signin/denied',
    title: 'Access is by invitation only',
    headingClass: 'sm-status-title',
    screenLabel: 'Sign in denied',
    navSection: null,
    shell: false,
    contractRoute: true,
  },
  {
    id: 'signin-error',
    pattern: '/signin/error',
    title: "Sign-in didn't finish",
    headingClass: 'sm-status-title sm-status-title--20',
    screenLabel: 'Sign in error',
    navSection: null,
    shell: false,
    contractRoute: false,
  },
  // Catch-all (mock L1004) — MUST stay last; `matchRoute` falls through to it.
  {
    id: 'not-found',
    pattern: '*',
    title: 'Lost in the stacks',
    headingClass: `${TITLE} ${TITLE}--34 ${TITLE}--shadow-pink`,
    screenLabel: 'Page state',
    navSection: null,
    shell: true,
    contractRoute: true,
  },
];

/** The 20 routes ui-contract §1 enumerates. */
export const CONTRACT_ROUTES = ROUTES.filter((route) => route.contractRoute);

/** @type {Map<string, RouteDefinition>} */
const BY_ID = new Map(ROUTES.map((route) => [route.id, route]));

/**
 * Looks a route definition up by id.
 *
 * @param {string} id
 * @returns {RouteDefinition|undefined}
 */
export function routeById(id) {
  return BY_ID.get(id);
}

/* ── Hash parsing (mock `_qOf` / `_path`) ──────────────────────────────── */

/**
 * Strips the leading `#`, defaulting an empty hash to `/` — the mock's
 * `(location.hash||'#/').slice(1)`.
 *
 * @param {string} hash raw `location.hash`
 * @returns {string} the hash payload, e.g. `/albums/alb-x?state=error`
 */
export function normalizeHash(hash) {
  const raw = hash === '' || hash === '#' ? '#/' : hash;
  return raw.startsWith('#') ? raw.slice(1) : raw;
}

/**
 * The query STRING inside a hash payload (mock `_qOf`, without the parse).
 *
 * @param {string} hashPayload
 * @returns {string} e.g. `state=error` (no leading `?`); '' when absent
 */
export function queryStringOf(hashPayload) {
  const index = hashPayload.indexOf('?');
  return index < 0 ? '' : hashPayload.slice(index + 1);
}

/**
 * The mock's `_q()` — query params carried INSIDE the hash, not on the
 * document URL. `#/downloads?state=quota-warning` -> `state=quota-warning`.
 *
 * @param {string} hashPayload
 * @returns {URLSearchParams}
 */
export function queryOf(hashPayload) {
  return new URLSearchParams(queryStringOf(hashPayload));
}

/**
 * The mock's `_path()`: everything before `?`, defaulted to `/`, with a
 * trailing slash trimmed (except on the root itself).
 *
 * @param {string} hashPayload
 * @returns {string}
 */
export function pathOf(hashPayload) {
  const index = hashPayload.indexOf('?');
  let path = (index < 0 ? hashPayload : hashPayload.slice(0, index)) || '/';
  if (!path.startsWith('/')) path = `/${path}`;
  if (path !== '/' && path.endsWith('/')) path = path.slice(0, -1);
  return path;
}

/**
 * Matches a path against {@link ROUTES}, capturing `:param` segments.
 * Falls through to the `not-found` route — never returns null.
 *
 * @param {string} path
 * @returns {{ route: RouteDefinition, params: Record<string, string> }}
 */
export function matchRoute(path) {
  const segments = path.split('/').filter((s) => s !== '');
  for (const route of ROUTES) {
    if (route.pattern === '*') continue;
    const patternSegments = route.pattern.split('/').filter((s) => s !== '');
    if (patternSegments.length !== segments.length) continue;
    /** @type {Record<string, string>} */
    const params = {};
    let matched = true;
    for (let i = 0; i < patternSegments.length; i += 1) {
      const expected = /** @type {string} */ (patternSegments[i]);
      const actual = /** @type {string} */ (segments[i]);
      if (expected.startsWith(':')) {
        if (actual === '') {
          matched = false;
          break;
        }
        params[expected.slice(1)] = decodeURIComponent(actual);
        continue;
      }
      if (expected !== actual) {
        matched = false;
        break;
      }
    }
    if (matched) return { route, params };
  }
  const notFound = BY_ID.get('not-found');
  if (notFound === undefined) throw new Error('router: the not-found route is missing');
  return { route: notFound, params: {} };
}

/**
 * Full resolution of a raw `location.hash`.
 *
 * @param {string} hash
 * @returns {{
 *   route: RouteDefinition,
 *   params: Record<string, string>,
 *   path: string,
 *   query: URLSearchParams,
 *   queryString: string,
 *   hash: string,
 * }}
 */
export function resolve(hash) {
  const payload = normalizeHash(hash);
  const path = pathOf(payload);
  const { route, params } = matchRoute(path);
  return {
    route,
    params,
    path,
    query: queryOf(payload),
    queryString: queryStringOf(payload),
    hash: payload,
  };
}

/**
 * The mock's `_go(h)`. Accepts `#/queue`, `/queue` or `queue`.
 *
 * @param {string} target
 * @returns {void}
 */
export function navigate(target) {
  const payload = target.startsWith('#') ? target.slice(1) : target;
  window.location.hash = payload.startsWith('/') ? payload : `/${payload}`;
}

/* ── Page heading (ui-contract §3: `page-heading`, the focus target) ────── */

/**
 * Builds the route's `<h1 data-h1 tabindex="-1" data-testid="page-heading">`.
 * Every route has exactly one; {@link RouteAnnouncer} focuses it after nav.
 *
 * @param {RouteDefinition} route
 * @param {string} [text] override for data-derived titles (artist/album name)
 * @returns {HTMLElement}
 */
export function PageHeading(route, text) {
  return el('h1', {
    className: route.headingClass,
    text: text ?? route.title,
    testId: TESTIDS.PAGE_HEADING,
    attrs: { tabindex: '-1', 'data-h1': '' },
  });
}

/* ── The hashchange listener (mock L871-872, L876) ─────────────────────── */

/**
 * @typedef {object} RouterHandle
 * @property {() => void} start begins listening and fires one initial resolve
 * @property {() => void} stop removes the listener (mock `componentWillUnmount`)
 * @property {() => ReturnType<typeof resolve>} current last resolved route
 * @property {(target: string) => void} redirect replace the current history
 *   entry with `target` and render it, instead of pushing (see below)
 */

/** A redirect must not chain forever if the route table is ever misconfigured. */
const REDIRECT_DEPTH_MAX = 5;

/**
 * Creates the hash router.
 *
 * `onNavigate` is invoked once at {@link RouterHandle.start} and then on every
 * `hashchange` — the mock's `this._onHash` (L871).
 *
 * @param {{
 *   onNavigate: (resolved: ReturnType<typeof resolve>) => void,
 *   target?: Window,
 * }} options
 * @returns {RouterHandle}
 */
export function createRouter(options) {
  const { onNavigate, target = window } = options;
  let current = resolve(target.location.hash);

  const render = (/** @type {ReturnType<typeof resolve>} */ next) => {
    current = next;
    onNavigate(next);
  };

  /**
   * REPLACE-not-push navigation (#back). A hub redirect or a guard/permission
   * bounce that PUSHES leaves the entry it redirects FROM sitting in history as
   * a Back-trap: one Back lands on it and it immediately bounces forward again,
   * so the entry behind it is unreachable (audit: `/browse` on the main nav,
   * the admin bounce, the deleted-playlist detail, the sign-in redirect).
   *
   * Replacing the current entry collapses the redirect to a single hop, so Back
   * skips straight over it. `replaceState` fires no `hashchange`, so the
   * destination is resolved and rendered here directly. `history.state` is
   * preserved so a sentinel the overlay stack recorded is not dropped.
   *
   * @param {string} rawTarget `#/x`, `/x` or `x`
   * @param {number} [depth]
   */
  function redirect(rawTarget, depth = 0) {
    const payload = rawTarget.startsWith('#') ? rawTarget.slice(1) : rawTarget;
    const hash = `#${payload.startsWith('/') ? payload : `/${payload}`}`;
    target.history.replaceState(target.history.state ?? null, '', hash);
    const next = resolve(target.location.hash);
    // A hub that points at another hub — collapse that too, but never loop.
    if (next.route.redirectTo !== undefined && depth < REDIRECT_DEPTH_MAX) {
      redirect(next.route.redirectTo, depth + 1);
      return;
    }
    render(next);
  }

  const handleHashChange = () => {
    const next = resolve(target.location.hash);
    // Redirect hubs (`/browse`, `/admin`) bounce before anything renders — in
    // place, so the hub entry the browser just pushed does not become a
    // Back-trap (#back).
    if (next.route.redirectTo !== undefined) {
      redirect(next.route.redirectTo);
      return;
    }
    render(next);
  };

  return {
    start() {
      target.addEventListener('hashchange', handleHashChange);
      handleHashChange();
    },
    stop() {
      target.removeEventListener('hashchange', handleHashChange);
    },
    current() {
      return current;
    },
    redirect,
  };
}
